/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Tooltip,
  Tag,
  Empty,
  Spin,
  Statistic,
  Row,
  Col,
  Table,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import moment from 'moment';
import {
  queryVitalityDailyTimeline,
  queryUserInStoreRecords,
  queryXplanCourseList,
} from './api';
import ActionOverviewDialog from './ActionOverviewDialog';
import FitTrackMap from './FitTrackMap';
import './DailyActivityDialog.less';

// ========== 工具函数 ==========
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  let result = '';
  if (hours > 0) result += `${hours}小时`;
  if (minutes > 0) result += `${minutes}分钟`;
  if (secs > 0) result += `${secs}秒`;
  return result || '-';
};

// ========== 事件类型映射（接口 eventCode -> 中文名/颜色/形状） ==========
// shape: diamond(菱形) | circle(圆形) | triangle(三角形) — 仅瞬时事件使用
const EVENT_TYPE_MAP = {
  ENTER_STORE: { name: '刷门禁', color: '#1890ff', shape: 'diamond' },
  TREADMILL: { name: '跑步机', color: '#52c41a' },
  GROUP_CLASS: { name: '团课', color: '#722ed1' },
  PERSONAL_TRAINING: { name: '私教', color: '#722ed1' },
  CHECK_IN: { name: '小程序签到', color: '#13c2c2', shape: 'circle' },
  CHECKOUT: { name: '离店打卡', color: '#eb2f96', shape: 'triangle' },
  FIT_FILE: { name: '位置更新', color: '#13c2c2' },
  XPLAN: { name: '入镜指导', color: '#fa8c16' },
};

const getEventTypeInfo = (eventCode) =>
  EVENT_TYPE_MAP[eventCode] || { name: eventCode || '未知', color: '#999' };

// 审核状态映射（与 detailBackup.jsx 保持一致）
const reviewStatusTextMap = {
  INIT: '等待处理',
  PASS: '审核通过',
  PENDING: '待审核',
  NOT_APPROVED: '审核不通过',
  REVOKED: '已撤销',
};

const reviewStatusColorMap = {
  PASS: 'green',
  PENDING: 'orange',
  NOT_APPROVED: 'red',
  REVOKED: 'default',
};

const getReviewStatusText = (status) =>
  reviewStatusTextMap[status] || '未知状态';
const getReviewStatusColor = (status) =>
  reviewStatusColorMap[status] || 'default';

// 到店训练事件类型映射（与 detailBackup.jsx queryUserInStoreRecords 接口字段一致）
const storeEventTypeTextMap = {
  offline_training_event: '到店训练',
  offline_treadmill_event: '跑步机',
  offline_group_class_event: '团课',
};

const storeEventTypeColorMap = {
  offline_training_event: 'blue',
  offline_treadmill_event: 'green',
  offline_group_class_event: 'orange',
};

const getStoreEventTypeText = (type) =>
  storeEventTypeTextMap[type] || '未知类型';
const getStoreEventTypeColor = (type) =>
  storeEventTypeColorMap[type] || 'default';

// queryUserInStoreRecords 的 eventMessageType -> 时间轴事件 eventCode 映射（用于风险信息匹配）
const STORE_MESSAGE_TYPE_TO_EVENT_CODE = {
  offline_treadmill_event: 'TREADMILL',
  offline_training_event: 'PERSONAL_TRAINING',
  offline_group_class_event: 'GROUP_CLASS',
};

// ========== 甘特图辅助函数 ==========
// 瞬时事件邻近判定的时间窗口（毫秒），在此窗口内的瞬时事件视为重叠
const INSTANT_EVENT_VICINITY_MS = 10 * 60 * 1000;

const hasEventOverlap = (event1, event2) => {
  if (!event1 || !event2) return false;

  const isInstant1 = event1.eventCategory === 'POINT' || event1.isInstant;
  const isInstant2 = event2.eventCategory === 'POINT' || event2.isInstant;
  const buffer = INSTANT_EVENT_VICINITY_MS / 2;

  const e1Start = isInstant1 ? event1.happenTime - buffer : event1.happenTime;
  const e1End = isInstant1
    ? event1.happenTime + buffer
    : event1.happenTime + (event1.durationSeconds || 0) * 1000;
  const e2Start = isInstant2 ? event2.happenTime - buffer : event2.happenTime;
  const e2End = isInstant2
    ? event2.happenTime + buffer
    : event2.happenTime + (event2.durationSeconds || 0) * 1000;

  return e1Start < e2End && e2Start < e1End;
};

const assignTrackTooltipEvents = (trackBlocks) => {
  if (!Array.isArray(trackBlocks) || trackBlocks.length === 0) return [];
  const sortedBlocks = [...trackBlocks].sort(
    (a, b) => a.happenTime - b.happenTime,
  );
  return sortedBlocks.map((block, idx) => {
    const others = sortedBlocks.filter(
      (b, j) => j !== idx && hasEventOverlap(block, b),
    );
    const tooltipEvents = [block, ...others]
      .sort((a, b) => a.happenTime - b.happenTime)
      .map((e, i) => ({
        ...e,
        tooltipKey: `${e.eventId || 'evt'}-${e.happenTime}-${i}`,
      }));
    return { ...block, tooltipEvents };
  });
};

/**
 * 贪心泳道分配算法：将重叠的事件分配到不同子行，避免视觉遮挡
 * @returns { lanes: number, blocks: 带 laneIndex 的 blocks 数组 }
 */
const assignLanes = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return { lanes: 1, blocks };
  }

  const sorted = [...blocks].sort((a, b) => a.happenTime - b.happenTime);
  const lanes = []; // lanes[i] = 该泳道最后一个 block 的结束时间

  const assigned = sorted.map((block) => {
    const blockStart = block.happenTime;
    const blockEnd = block.happenTime + (block.durationSeconds || 0) * 1000;

    // 找到第一个可用的泳道（该泳道最后一个 block 在 blockStart 之前结束）
    let assignedLane = -1;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i] <= blockStart) {
        assignedLane = i;
        break;
      }
    }

    if (assignedLane === -1) {
      // 没有可用泳道，新增一个
      assignedLane = lanes.length;
      lanes.push(blockEnd);
    } else {
      lanes[assignedLane] = blockEnd;
    }

    return { ...block, laneIndex: assignedLane };
  });

  return { lanes: lanes.length, blocks: assigned };
};

// ========== 聚合 Tooltip ==========
const AggregatedTooltip = ({ events, children }) => {
  if (!events || events.length === 0) return children;

  const content = (
    <div className="gantt-tooltip-content">
      {events.length > 1 && (
        <div className="tooltip-header">
          <span className="tooltip-count">{events.length} 个事件</span>
        </div>
      )}
      {events.map((event, idx) => {
        const info = getEventTypeInfo(event.eventCode);
        const isInstant = event.eventCategory === 'POINT';
        return (
          <div key={event.tooltipKey || idx} className="tooltip-event-item">
            <div className="tooltip-event-type" style={{ color: info.color }}>
              {event.eventName || info.name}
            </div>
            {event.storeName && (
              <div className="tooltip-event-name">{event.storeName}</div>
            )}
            <div className="tooltip-event-time">
              {moment(event.happenTime).format('HH:mm')}
              {!isInstant && event.durationSeconds > 0 && (
                <>
                  {' - '}
                  {moment(
                    event.happenTime + event.durationSeconds * 1000,
                  ).format('HH:mm')}
                </>
              )}
            </div>
            {!isInstant && event.durationSeconds > 0 && (
              <div className="tooltip-event-detail">
                {formatDuration(event.durationSeconds)}
              </div>
            )}
            {idx < events.length - 1 && <div className="tooltip-divider" />}
          </div>
        );
      })}
    </div>
  );

  return (
    <Tooltip
      placement="top"
      title={content}
      overlayClassName="gantt-tooltip-overlay"
    >
      {children}
    </Tooltip>
  );
};

// ========== 主组件 ==========
const DailyActivityDialog = ({ visible, onClose, userId, queryDate }) => {
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [actionOverviewVisible, setActionOverviewVisible] = useState(false);
  const [selectedXplanRecord, setSelectedXplanRecord] = useState(null);
  const [fitTrackVisible, setFitTrackVisible] = useState(false);
  const [selectedFitUrl, setSelectedFitUrl] = useState('');
  const [selectedFitPointCount, setSelectedFitPointCount] = useState(0);

  // 当天训练明细（到店 + 入镜）
  const [storeTrainingRecords, setStoreTrainingRecords] = useState([]);
  const [xplanTrainingRecords, setXplanTrainingRecords] = useState([]);
  const [trainingLoading, setTrainingLoading] = useState(false);

  // 弹窗打开时调用接口
  useEffect(() => {
    if (!visible || !userId || !queryDate) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await queryVitalityDailyTimeline({
          requestData: { userId: Number(userId), queryDate },
        });
        if (res?.code === '00000' && res?.data) {
          setApiData(res.data);
        } else {
          message.error(res?.msg || '获取活动详情失败');
          setApiData(null);
        }
      } catch (error) {
        console.error('获取活动详情失败:', error);
        message.error('获取活动详情失败');
        setApiData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [visible, userId, queryDate]);

  // 当天训练明细接口（到店 + 入镜）
  useEffect(() => {
    if (!visible || !userId || !queryDate) return;

    const fetchTrainingData = async () => {
      setTrainingLoading(true);
      const startTime = moment(queryDate).startOf('day').valueOf();
      const endTime = moment(queryDate).endOf('day').valueOf();

      try {
        const [storeRes, xplanRes] = await Promise.all([
          queryUserInStoreRecords({
            requestData: { userId: Number(userId), startTime, endTime },
          }),
          queryXplanCourseList({
            requestData: { userId: Number(userId), startTime, endTime },
          }),
        ]);

        if (storeRes?.code === '00000') {
          setStoreTrainingRecords(storeRes?.data || []);
        } else {
          setStoreTrainingRecords([]);
        }

        if (xplanRes?.code === '00000') {
          setXplanTrainingRecords(xplanRes?.data?.courses || []);
        } else {
          setXplanTrainingRecords([]);
        }
      } catch (error) {
        console.error('获取训练明细失败:', error);
        setStoreTrainingRecords([]);
        setXplanTrainingRecords([]);
      } finally {
        setTrainingLoading(false);
      }
    };

    fetchTrainingData();
  }, [visible, userId, queryDate]);

  useEffect(() => {
    if (visible) setIsTimelineExpanded(true);
  }, [visible]);

  // ========== 数据转换 ==========

  // 入镜详情点击
  const handleXplanClick = useCallback((xplanEvent) => {
    setSelectedXplanRecord({
      roomId: xplanEvent.roomId,
      courseName: xplanEvent.courseName || xplanEvent.eventName,
      classTime: xplanEvent.happenTime
        ? moment(xplanEvent.happenTime).format('YYYY-MM-DD HH:mm:ss')
        : '-',
      durationSeconds: xplanEvent.durationSeconds,
    });
    setActionOverviewVisible(true);
  }, []);

  // 训练明细表格中入镜行点击
  const handleTrainingRowClick = useCallback((record) => {
    if (record.sourceType !== 'xplan') return;
    setSelectedXplanRecord({
      roomId: record.roomId,
      courseName: record.courseName,
      classTime: record.startTime
        ? moment(record.startTime).format('YYYY-MM-DD HH:mm:ss')
        : '-',
      durationSeconds: record.durationSeconds,
    });
    setActionOverviewVisible(true);
  }, []);

  // 位置轨迹点击
  const handleFitClick = useCallback((fitEvent) => {
    if (fitEvent.fileUrl) {
      setSelectedFitUrl(fitEvent.fileUrl);
      setSelectedFitPointCount(fitEvent.pointCount || 0);
      setFitTrackVisible(true);
    }
  }, []);

  // 所有门店事件展开（带 storeName）
  const allStoreEvents = useMemo(() => {
    if (!apiData?.storeVisitGroups) return [];
    const result = [];
    apiData.storeVisitGroups.forEach((group) => {
      (group.events || []).forEach((event) => {
        result.push({
          ...event,
          storeName: group.storeName,
          storeId: group.storeId,
        });
      });
    });
    return result;
  }, [apiData]);

  // 入镜课程事件（统一 timestamp 字段名）
  const xplanEvents = useMemo(() => {
    if (!apiData?.xplanCourses) return [];
    return apiData.xplanCourses.map((course) => ({
      eventCode: 'XPLAN',
      eventName: course.title || '入镜指导',
      eventId: course.roomId,
      eventCategory: 'DURATION',
      happenTime: course.startTime,
      endTime: course.endTime,
      durationSeconds: course.classDuration,
      courseName: course.title,
      roomId: course.roomId,
      meetStandard: course.meetStandard,
    }));
  }, [apiData]);

  // fitFiles 位置更新事件
  const fitFileEvents = useMemo(() => {
    if (!apiData?.fitFiles) return [];
    return apiData.fitFiles.map((file, idx) => ({
      eventCode: 'FIT_FILE',
      eventName: `位置更新${apiData.fitFiles.length > 1 ? ` #${idx + 1}` : ''}`,
      eventId: `fit-${idx}`,
      eventCategory: 'DURATION',
      happenTime: file.startTime,
      endTime: file.endTime,
      durationSeconds: Math.round((file.endTime - file.startTime) / 1000),
      fileUrl: file.fileUrl,
      pointCount: file.pointCount,
    }));
  }, [apiData]);

  // 所有事件合并（用于时间轴）
  const allEvents = useMemo(() => {
    return [...allStoreEvents, ...xplanEvents, ...fitFileEvents].sort(
      (a, b) => a.happenTime - b.happenTime,
    );
  }, [allStoreEvents, xplanEvents, fitFileEvents]);

  // ========== 当天训练明细（到店 + 入镜，使用 detailBackup 相同接口） ==========
  const combinedTrainingRecords = useMemo(() => {
    const records = [];

    // 到店训练记录（queryUserInStoreRecords）
    (storeTrainingRecords || []).forEach((item) => {
      records.push({
        key: `store-${item.eventId || item.startTime}`,
        startTime: item.startTime,
        eventType: 'store',
        eventMessageType: item.eventMessageType,
        storeName: item.storeName || '-',
        courseName: '-',
        durationSeconds: item.durationSeconds,
        meetStandard: item.meetStandard,
        reviewStatus: item.reviewStatus,
        riskControlType: item.riskControlType,
        riskControlDesc: item.riskControlDesc,
        sourceType: 'store',
      });
    });

    // 入镜训练记录（queryXplanCourseList）
    (xplanTrainingRecords || []).forEach((item) => {
      records.push({
        key: `xplan-${item.roomId || item.classTime}`,
        startTime: item.classTime,
        eventType: 'xplan',
        eventMessageType: null,
        storeName: '-',
        courseName: item.courseName || '入镜指导',
        durationSeconds: item.durationSeconds,
        meetStandard: item.meetStandard,
        reviewStatus: 'PASS', // 入镜直接审核通过
        sourceType: 'xplan',
        roomId: item.roomId,
      });
    });

    // 按开始时间排序
    records.sort((a, b) => (a.startTime || 0) - (b.startTime || 0));
    return records;
  }, [storeTrainingRecords, xplanTrainingRecords]);

  // 在 queryUserInStoreRecords 记录中查找与时间轴事件匹配的风险记录
  // 匹配策略：事件类型相同的前提下，优先取开始时间最接近的记录，其次取时间段重叠的记录
  const getStoreEventRiskRecord = useCallback(
    (event) => {
      if (!event || !storeTrainingRecords?.length) return null;

      const matchedType = Object.keys(STORE_MESSAGE_TYPE_TO_EVENT_CODE).find(
        (type) => STORE_MESSAGE_TYPE_TO_EVENT_CODE[type] === event.eventCode,
      );
      if (!matchedType) return null;

      const eventStart = event.happenTime;
      if (!eventStart) return null;
      const eventEnd = eventStart + (event.durationSeconds || 0) * 1000;

      let best = null;
      let bestStartDiff = Infinity;
      let bestOverlap = -1;

      storeTrainingRecords.forEach((record) => {
        if (record.eventMessageType !== matchedType) return;

        const startTime = record.startTime || 0;
        const endTime = record.endTime || startTime;
        // 个别异常数据 startTime > endTime，此时用 endTime 反推真实开始时间
        const recStart =
          startTime <= endTime
            ? startTime
            : endTime - (record.durationSeconds || 0) * 1000;
        const recEnd = recStart + (record.durationSeconds || 0) * 1000;

        const overlap =
          Math.min(eventEnd, recEnd) - Math.max(eventStart, recStart);
        if (overlap <= 0) return;

        const startDiff = Math.abs(eventStart - recStart);
        const isBetter =
          !best ||
          startDiff < bestStartDiff ||
          (startDiff === bestStartDiff && overlap > bestOverlap);

        if (isBetter) {
          best = record;
          bestStartDiff = startDiff;
          bestOverlap = overlap;
        }
      });

      // 仅当命中的记录确实带风险文案时才返回，避免渲染出空的“风险提示”行
      return best && best.riskControlDesc ? best : null;
    },
    [storeTrainingRecords],
  );

  // ========== 当天概况 ==========
  const summary = useMemo(() => {
    if (!apiData) return null;

    let minTime = null;
    let maxTime = null;
    allEvents.forEach((e) => {
      const ts = e.happenTime;
      if (!minTime || ts < minTime) minTime = ts;
      const end = ts + (e.durationSeconds || 0) * 1000;
      if (!maxTime || end > maxTime) maxTime = end;
    });

    return {
      date: queryDate,
      weekdayName: moment(queryDate).format('ddd'),
      inStoreCount: apiData.inStoreActivityCount || 0,
      xplanCount: apiData.onlineTrainingCount || 0,
      totalDuration: apiData.totalTrainingSeconds || 0,
      minTime,
      maxTime,
      locationCount: apiData.fitFiles?.length || 0,
    };
  }, [apiData, allEvents, queryDate]);

  // ========== 甘特图数据 ==========
  const ganttTracks = useMemo(() => {
    if (!apiData) return [];

    let minTime = null;
    let maxTime = null;
    allEvents.forEach((e) => {
      const ts = e.happenTime;
      if (!minTime || ts < minTime) minTime = ts;
      const end = ts + (e.durationSeconds || 0) * 1000;
      if (!maxTime || end > maxTime) maxTime = end;
    });

    if (!minTime || !maxTime) return [];

    // 扩展到前后 1 小时
    minTime -= 60 * 60 * 1000;
    maxTime += 60 * 60 * 1000;
    const totalDuration = maxTime - minTime || 1;

    const tracks = [];

    // 按门店分组轨道
    (apiData.storeVisitGroups || []).forEach((group) => {
      const blocks = (group.events || []).map((event) => {
        const info = getEventTypeInfo(event.eventCode);
        const isInstant = event.eventCategory === 'POINT';
        const startOffset = Math.max(0, event.happenTime - minTime);
        const duration = (event.durationSeconds || 0) * 1000;
        const isLast = startOffset >= totalDuration - 1000;

        if (isInstant) {
          return {
            ...event,
            storeName: group.storeName,
            left: (startOffset / totalDuration) * 100,
            width: 0,
            isInstant: true,
            isLast: false,
            color: info.color,
            visualStart: startOffset,
            visualEnd: startOffset,
            happenTime: event.happenTime,
            eventCode: event.eventCode,
          };
        }

        let leftPct = (startOffset / totalDuration) * 100;
        let widthPct = (duration / totalDuration) * 100;
        if (isLast) {
          leftPct = 100;
          widthPct = 0;
        } else if (leftPct + widthPct > 100) {
          widthPct = 100 - leftPct;
        }

        return {
          ...event,
          storeName: group.storeName,
          left: leftPct,
          width: Math.max(widthPct, 1),
          isInstant: false,
          isLast,
          color: info.color,
          visualStart: startOffset,
          visualEnd: startOffset + duration,
          happenTime: event.happenTime,
          eventCode: event.eventCode,
        };
      });

      const { lanes: storeLanes, blocks: lanedBlocks } = assignLanes(blocks);
      tracks.push({
        id: `store-${group.storeId || group.storeName}`,
        name: group.storeName || '未知门店',
        type: 'inStore',
        color: '#1890ff',
        lanes: storeLanes,
        blocks: assignTrackTooltipEvents(lanedBlocks),
      });
    });

    // 入镜训练合并为一个轨道
    if (xplanEvents.length > 0) {
      const xplanBlocks = xplanEvents.map((course) => {
        const startOffset = Math.max(0, course.happenTime - minTime);
        const duration = (course.durationSeconds || 0) * 1000;
        const isLast = startOffset >= totalDuration - 1000;
        let leftPct = (startOffset / totalDuration) * 100;
        let widthPct = (duration / totalDuration) * 100;

        if (isLast) {
          leftPct = 100;
          widthPct = 0;
        } else if (leftPct + widthPct > 100) {
          widthPct = 100 - leftPct;
        }

        return {
          ...course,
          left: leftPct,
          width: Math.max(widthPct, 1),
          isInstant: false,
          isLast,
          color: '#fa8c16',
          visualStart: startOffset,
          visualEnd: startOffset + duration,
        };
      });

      const { lanes: xplanLanes, blocks: xplanLanedBlocks } =
        assignLanes(xplanBlocks);
      tracks.push({
        id: 'xplan',
        name: '入镜指导',
        type: 'xplan',
        color: '#fa8c16',
        lanes: xplanLanes,
        blocks: assignTrackTooltipEvents(xplanLanedBlocks),
      });
    }

    // 位置更新轨道（fitFiles）
    if (apiData.fitFiles && apiData.fitFiles.length > 0) {
      const fitBlocks = apiData.fitFiles.map((file, idx) => {
        const startOffset = Math.max(0, file.startTime - minTime);
        const duration = file.endTime - file.startTime;
        const isLast = startOffset >= totalDuration - 1000;
        let leftPct = (startOffset / totalDuration) * 100;
        let widthPct = (duration / totalDuration) * 100;

        if (isLast) {
          leftPct = 100;
          widthPct = 0;
        } else if (leftPct + widthPct > 100) {
          widthPct = 100 - leftPct;
        }

        return {
          eventCode: 'FIT_FILE',
          eventName: `位置更新${
            apiData.fitFiles.length > 1 ? ` #${idx + 1}` : ''
          }`,
          eventId: `fit-${idx}`,
          eventCategory: 'DURATION',
          happenTime: file.startTime,
          endTime: file.endTime,
          durationSeconds: Math.round(duration / 1000),
          fileUrl: file.fileUrl,
          pointCount: file.pointCount,
          left: leftPct,
          width: Math.max(widthPct, 1),
          isInstant: false,
          isLast,
          color: '#13c2c2',
          visualStart: startOffset,
          visualEnd: startOffset + duration,
        };
      });

      const { lanes: fitLanes, blocks: fitLanedBlocks } =
        assignLanes(fitBlocks);
      tracks.push({
        id: 'fitFiles',
        name: '位置更新',
        type: 'fitFiles',
        color: '#13c2c2',
        lanes: fitLanes,
        blocks: assignTrackTooltipEvents(fitLanedBlocks),
      });
    }

    return { tracks, minTime, maxTime, totalDuration };
  }, [apiData, allEvents, xplanEvents]);

  // 甘特图时间范围
  const ganttTimeRange = useMemo(() => {
    if (!ganttTracks?.minTime || !ganttTracks?.maxTime)
      return { start: '', end: '' };
    return {
      start: moment(ganttTracks.minTime).format('HH:mm'),
      end: moment(ganttTracks.maxTime).format('HH:mm'),
    };
  }, [ganttTracks]);

  // 甘特图时间刻度（每 2 小时）
  const ganttTicks = useMemo(() => {
    if (!ganttTracks?.minTime || !ganttTracks?.maxTime) return [];
    const { minTime, maxTime, totalDuration } = ganttTracks;

    const ticks = [];
    const intervalMs = 2 * 60 * 60 * 1000;
    let current = Math.ceil(minTime / intervalMs) * intervalMs;

    while (current < maxTime) {
      ticks.push({
        time: moment(current).format('HH:mm'),
        position: ((current - minTime) / totalDuration) * 100,
      });
      current += intervalMs;
    }

    return ticks;
  }, [ganttTracks]);

  // 图例 - 所有出现的事件类型
  const activeEventCodes = useMemo(() => {
    const codes = new Set();
    allStoreEvents.forEach((e) => codes.add(e.eventCode));
    if (xplanEvents.length > 0) codes.add('XPLAN');
    if (apiData?.fitFiles?.length > 0) codes.add('FIT_FILE');
    return Array.from(codes);
  }, [allStoreEvents, xplanEvents, apiData]);

  // ========== 当天训练明细表格列 ==========
  const trainingColumns = useMemo(
    () => [
      {
        title: '开始时间',
        dataIndex: 'startTime',
        key: 'startTime',
        width: 160,
        render: (v) => (v ? moment(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
      },
      {
        title: '事件类型',
        dataIndex: 'eventMessageType',
        key: 'eventMessageType',
        width: 120,
        render: (v, record) => {
          if (record.sourceType === 'xplan') {
            return <Tag color="#fa8c16">入镜指导</Tag>;
          }
          return (
            <Tag color={getStoreEventTypeColor(v)}>
              {getStoreEventTypeText(v)}
              {record.riskControlType === 'reapply' ? '(补卡)' : ''}
            </Tag>
          );
        },
      },
      {
        title: '门店名称/课程名称',
        key: 'name',
        width: 180,
        ellipsis: true,
        render: (_, record) => {
          if (record.sourceType === 'xplan') {
            return record.courseName || '-';
          }
          return record.storeName || '-';
        },
      },
      {
        title: '持续时间',
        dataIndex: 'durationSeconds',
        key: 'durationSeconds',
        width: 120,
        render: (v) => formatDuration(v),
      },
      {
        title: '达标状态',
        dataIndex: 'meetStandard',
        key: 'meetStandard',
        width: 100,
        render: (v, record) => {
          if (v === undefined || v === null) return <span>-</span>;
          return (
            <span>
              <Tag color={v ? 'green' : 'red'}>{v ? '达标' : '未达标'}</Tag>
              {record.riskControlDesc && (
                <Tooltip title={record.riskControlDesc}>
                  <ExclamationCircleFilled
                    style={{ color: '#fa8c16', cursor: 'help' }}
                  />
                </Tooltip>
              )}
            </span>
          );
        },
      },
      {
        title: '审核状态',
        dataIndex: 'reviewStatus',
        key: 'reviewStatus',
        width: 110,
        render: (v) => (
          <Tag color={getReviewStatusColor(v)}>{getReviewStatusText(v)}</Tag>
        ),
      },
    ],
    [],
  );

  const renderTrainingTable = () => {
    return (
      <div className="training-table-section">
        <div className="section-subtitle">当天训练明细</div>
        <div className="table-wrapper">
          <Spin spinning={trainingLoading}>
            <Table
              dataSource={combinedTrainingRecords}
              columns={trainingColumns}
              size="small"
              pagination={false}
              scroll={
                combinedTrainingRecords.length > 0 ? { x: 790 } : undefined
              }
              locale={{
                emptyText: <Empty description="当天没有训练记录" />,
              }}
              rowClassName={(record) =>
                record.sourceType === 'xplan' ? 'xplan-row-clickable' : ''
              }
              onRow={(record) => ({
                onClick: () => handleTrainingRowClick(record),
              })}
            />
          </Spin>
        </div>
      </div>
    );
  };

  // ========== 渲染 ==========

  const renderSummary = () => {
    if (!summary) return null;
    return (
      <div className="summary-section">
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="到店活动"
              value={summary.inStoreCount}
              suffix="次"
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="入镜训练"
              value={summary.xplanCount}
              suffix="次"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={8}>
            <div className="summary-status">
              <div className="status-label">活动状态</div>
              <div className="status-value">
                {allEvents.length > 0 ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    有活动记录
                  </Tag>
                ) : (
                  <Tag>无记录</Tag>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <div className="summary-info">
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              活动时间：
              {summary.minTime && summary.maxTime
                ? `${moment(summary.minTime).format('HH:mm')} - ${moment(
                    summary.maxTime,
                  ).format('HH:mm')}`
                : '-'}
            </div>
          </Col>
          <Col span={8}>
            <div className="summary-info">
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              总时长：{formatDuration(summary.totalDuration)}
            </div>
          </Col>
          <Col span={8}>
            <div className="summary-info">
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              位置更新文件：{summary.locationCount}个
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderGanttLegend = () => {
    if (!activeEventCodes.length) return null;

    const durationCodes = activeEventCodes.filter(
      (code) => !getEventTypeInfo(code).shape,
    );
    const instantCodes = activeEventCodes.filter(
      (code) => getEventTypeInfo(code).shape,
    );

    return (
      <div className="gantt-legend">
        {durationCodes.map((code) => {
          const info = getEventTypeInfo(code);
          return (
            <span key={code} className="legend-item">
              <span
                className="legend-dot"
                style={{ backgroundColor: info.color }}
              />
              {info.name}
            </span>
          );
        })}
        {instantCodes.map((code) => {
          const info = getEventTypeInfo(code);
          return (
            <span key={code} className="legend-item">
              <span
                className={`legend-shape legend-shape-${info.shape}`}
                style={
                  info.shape === 'triangle'
                    ? { borderBottomColor: info.color }
                    : { backgroundColor: info.color }
                }
              />
              {info.name}
            </span>
          );
        })}
      </div>
    );
  };

  const renderGanttChart = () => {
    if (!ganttTracks?.tracks?.length) {
      return null;
    }

    const { tracks } = ganttTracks;

    return (
      <div className="gantt-section">
        <div className="section-subtitle">活动时间分布</div>
        {renderGanttLegend()}
        <div className="gantt-chart">
          <div className="gantt-header">
            <div className="gantt-track-name-header" />
            <div className="gantt-timeline-area">
              <div className="gantt-time-labels">
                <span className="time-label">{ganttTimeRange.start}</span>
                <span className="time-label">{ganttTimeRange.end}</span>
              </div>
              <div className="gantt-ticks">
                {ganttTicks.map((tick, idx) => (
                  <div
                    key={idx}
                    className="gantt-tick"
                    style={{ left: `${tick.position}%` }}
                  >
                    <span className="tick-label">{tick.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="gantt-body">
            {tracks.map((track) => {
              const lanes = track.lanes || 1;
              const laneHeight = 22;
              const trackBarHeight = lanes * laneHeight;

              return (
                <div key={track.id} className="gantt-track">
                  <div
                    className="gantt-track-name"
                    style={{ height: trackBarHeight }}
                  >
                    <span
                      className="track-type-indicator"
                      style={{ backgroundColor: track.color }}
                    />
                    <span className="track-name-text">{track.name}</span>
                  </div>

                  <div
                    className="gantt-track-bars"
                    style={{ height: trackBarHeight }}
                  >
                    {ganttTicks.map((tick, idx) => (
                      <div
                        key={idx}
                        className="track-grid-line"
                        style={{ left: `${tick.position}%` }}
                      />
                    ))}

                    {/* 泳道分隔线 */}
                    {lanes > 1 &&
                      Array.from({ length: lanes - 1 }, (_, i) => (
                        <div
                          key={`lane-sep-${i}`}
                          className="track-lane-separator"
                          style={{ top: (i + 1) * laneHeight }}
                        />
                      ))}

                    {track.blocks?.map((block, idx) => {
                      const hasTooltip =
                        block.tooltipEvents && block.tooltipEvents.length > 0;
                      const tooltipEvents = hasTooltip
                        ? block.tooltipEvents
                        : [block];
                      const isXplan = track.type === 'xplan';
                      const isFitFile = track.type === 'fitFiles';
                      const laneIdx = block.laneIndex || 0;

                      const handleBlockClick = isXplan
                        ? (e) => {
                            e.stopPropagation();
                            handleXplanClick(block);
                          }
                        : isFitFile
                        ? (e) => {
                            e.stopPropagation();
                            handleFitClick(block);
                          }
                        : undefined;

                      if (block.isInstant) {
                        const instantInfo = getEventTypeInfo(block.eventCode);
                        const shape = instantInfo.shape || 'diamond';

                        return (
                          <AggregatedTooltip
                            key={`instant-${idx}`}
                            events={tooltipEvents}
                          >
                            <div
                              className="track-instant-event"
                              style={{
                                left: `${block.left}%`,
                                top: laneIdx * laneHeight,
                                height: laneHeight,
                                ...(isXplan || isFitFile
                                  ? { cursor: 'pointer' }
                                  : {}),
                              }}
                              onClick={handleBlockClick}
                            >
                              <div
                                className={`track-instant-marker track-instant-${shape}`}
                                style={
                                  shape === 'triangle'
                                    ? { borderBottomColor: block.color }
                                    : { backgroundColor: block.color }
                                }
                              />
                              <div
                                className="track-instant-line"
                                style={{ borderLeftColor: block.color }}
                              />
                            </div>
                          </AggregatedTooltip>
                        );
                      }

                      return (
                        <AggregatedTooltip
                          key={`bar-${idx}`}
                          events={tooltipEvents}
                        >
                          <div
                            className={`track-block${
                              block.isLast ? ' track-block-last' : ''
                            }`}
                            style={{
                              left: `${block.left}%`,
                              width: `${block.width}%`,
                              top: laneIdx * laneHeight + 2,
                              height: laneHeight - 4,
                              backgroundColor: block.color,
                              ...(isXplan || isFitFile
                                ? { cursor: 'pointer' }
                                : {}),
                            }}
                            onClick={handleBlockClick}
                          >
                            {!block.isLast && block.width > 4 && (
                              <span className="track-block-label">
                                {getEventTypeInfo(block.eventCode).name}
                              </span>
                            )}
                          </div>
                        </AggregatedTooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderTimeline = () => {
    if (!allEvents.length) {
      return null;
    }

    return (
      <div className="timeline-section">
        <div
          className="timeline-collapse-header"
          onClick={() => setIsTimelineExpanded((prev) => !prev)}
        >
          <div className="section-subtitle" style={{ marginBottom: 0 }}>
            活动明细记录
          </div>
          <div className="toggle-btn">
            <span>{isTimelineExpanded ? '收起明细' : '查看明细'}</span>
          </div>
        </div>

        {isTimelineExpanded && (
          <div className="timeline-collapse-content">
            <div className="custom-timeline">
              {allEvents.map((event, index) => {
                const info = getEventTypeInfo(event.eventCode);
                const isInstant = event.eventCategory === 'POINT';
                const riskRecord = getStoreEventRiskRecord(event);

                return (
                  <div key={index} className="timeline-item">
                    <div className="timeline-item-tail">
                      <div
                        className="timeline-item-dot"
                        style={{ backgroundColor: info.color }}
                      />
                    </div>
                    <div
                      className="timeline-item-body"
                      onClick={
                        event.eventCode === 'XPLAN'
                          ? (e) => {
                              e.stopPropagation();
                              handleXplanClick(event);
                            }
                          : event.eventCode === 'FIT_FILE'
                          ? (e) => {
                              e.stopPropagation();
                              handleFitClick(event);
                            }
                          : undefined
                      }
                      style={
                        event.eventCode === 'XPLAN' ||
                        event.eventCode === 'FIT_FILE'
                          ? { cursor: 'pointer' }
                          : undefined
                      }
                    >
                      <div className="timeline-item-time">
                        {moment(event.happenTime).format('HH:mm')}
                      </div>
                      <div className="timeline-event-content">
                        <div className="event-header">
                          <Tag color={info.color}>
                            {event.eventName || info.name}
                          </Tag>
                          {event.storeName && (
                            <span className="event-name">
                              {event.storeName}
                            </span>
                          )}
                          {event.courseName && (
                            <span className="event-name">
                              {event.courseName}
                            </span>
                          )}
                        </div>
                        <div className="event-details">
                          {!isInstant && event.endTime && (
                            <div className="detail-row">
                              <span className="detail-label">时间段：</span>
                              <span className="detail-value">
                                {moment(event.happenTime).format('HH:mm')} -{' '}
                                {moment(event.endTime).format('HH:mm')}
                              </span>
                            </div>
                          )}
                          {event.pointCount !== undefined && (
                            <div className="detail-row">
                              <span className="detail-label">定位点数：</span>
                              <span className="detail-value">
                                {event.pointCount}
                              </span>
                            </div>
                          )}
                          {!isInstant && !event.fileUrl && (
                            <div className="detail-row">
                              <span className="detail-label">持续时间：</span>
                              <span className="detail-value">
                                {formatDuration(event.durationSeconds)}
                              </span>
                            </div>
                          )}
                          {riskRecord && (
                            <div className="detail-row">
                              <span className="detail-label">风险提示：</span>
                              <span
                                className="detail-value risk-value"
                                style={
                                  riskRecord.riskControlType === 'reapply'
                                    ? { color: '#faad14' }
                                    : undefined
                                }
                              >
                                {riskRecord.riskControlDesc}
                              </span>
                            </div>
                          )}
                        </div>
                        {event.eventCode === 'XPLAN' && (
                          <div
                            className="detail-row"
                            style={{ color: '#fa8c16', marginTop: 4 }}
                          >
                            查看详情 &gt;
                          </div>
                        )}
                        {event.eventCode === 'FIT_FILE' && (
                          <div
                            className="detail-row"
                            style={{ color: '#13c2c2', marginTop: 4 }}
                          >
                            查看轨迹回放 &gt;
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        title={
          queryDate
            ? `${queryDate} ${moment(queryDate).format('dddd')} 活动详情`
            : '活动详情'
        }
        width={900}
        footer={null}
        destroyOnClose
        centered
        className="daily-activity-dialog"
      >
        <Spin spinning={loading}>
          <div className="daily-activity-content">
            {apiData ? (
              <>
                {renderSummary()}
                {renderGanttChart()}
                {renderTrainingTable()}
                {renderTimeline()}
              </>
            ) : !loading ? (
              <Empty description="暂无活动数据" />
            ) : null}
          </div>
        </Spin>
      </Modal>
      <ActionOverviewDialog
        visible={actionOverviewVisible}
        onClose={() => {
          setActionOverviewVisible(false);
          setSelectedXplanRecord(null);
        }}
        record={selectedXplanRecord}
      />
      <FitTrackMap
        visible={fitTrackVisible}
        onClose={() => {
          setFitTrackVisible(false);
          setSelectedFitUrl('');
          setSelectedFitPointCount(0);
        }}
        fileUrl={selectedFitUrl}
        pointCount={selectedFitPointCount}
      />
    </>
  );
};

export default DailyActivityDialog;
